import {DocumentNode} from "@apollo/client";
import {ReactNode} from "react";

export interface FilterOption {
  display: string;
  key: string;
}

export type MatchMode = "OR" | "AND";

export enum FilterCategoryType {
  ARRAY = "ARRAY",
  RANGE = "RANGE",
  GQL = "GQL",
}

export interface BaseFilterCategory<T extends FilterCategoryType, V> {
  type: T;
  key: string;
  display: ReactNode;
  sorted?: boolean;
  defaultValue?: V;
  matchMode?: {enabled: boolean; default?: MatchMode};
}

export interface ArrayFilterCategory extends BaseFilterCategory<FilterCategoryType.ARRAY, string[]> {
  options?: FilterOption[]; // is this only for ARRAY type?
}

export interface RangeFilterCategory extends BaseFilterCategory<FilterCategoryType.RANGE, RangeFilterType> {}

export interface GQLFilterCategory extends BaseFilterCategory<FilterCategoryType.GQL, string[]> {
  graphql: DocumentNode;
}

export type FilterCategory = ArrayFilterCategory | RangeFilterCategory | GQLFilterCategory;

export type RangeFilterType = {
  from: Date | null;
  to: Date | null;
} | null;

export type FilterValueType<C extends FilterCategory> = C extends ArrayFilterCategory
  ? string[]
  : C extends RangeFilterCategory
    ? RangeFilterType
    : C extends GQLFilterCategory
      ? string[]
      : null;

export type FilterSets<C extends FilterCategory> = {
  [K in C["key"]]?: FilterValueType<Extract<C, {key: K}>>;
};

export interface FilterDropdownProps<C extends FilterCategory> {
  categories: C[];
  appliedFilterSets: FilterSets<C>;
  onApply: (updatedCategories: FilterSets<C>) => void;
  loading?: boolean;
  defaultActiveCategoryId?: string;
  portal?: boolean;
  disabled?: boolean;
}

export const MAX_GQL_RECORDS = 100;

export type FilterDefinitionFieldOptions = {
  key: string;
  display: string;
};

export interface FilterData {
  total: number;
  items: FilterDefinitionFieldOptions[];
}

export interface FilterDataResults {
  values: FilterData;
}

export const encodeFilter = (filter: FilterSets<FilterCategory>) => encodeURIComponent(JSON.stringify(filter));

export const getMatchModeKey = (categoryKey: string) => `${categoryKey}_match` as const;

export const withMatchMode = <C extends FilterCategory>(
  filters: FilterSets<C>,
  categoryKey: C["key"],
  mode: MatchMode
): FilterSets<C> => ({...filters, [getMatchModeKey(categoryKey)]: mode});

// A type-safe accessor function
export const getFilterValue = <C extends FilterCategory>(
  filters: FilterSets<C>,
  key: C["key"]
): FilterValueType<Extract<C, {key: typeof key}>> | undefined => filters[key];
